/* eslint-disable max-statements */
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';

import { usePdpContext } from '../../pdp-provider';
import { NONE_VALUE } from '../../pdp-provider/transform-selected-options';
import SelectWrapper from './select-wrapper';
import messages from './messages';

const OneSelect = ({
  categoryKeyPath,
  topLevelCategoryName,
  topLevelCategoryNumber,
  productOptionCategory,
  useInactiveStyle,
}) => {
  const { formatMessage } = useIntl();
  const {
    isDemystifyMenuPdpEnabled,
    pricedSelectedOptions,
    pricedProductFullOptions,
    productOptionDefaults,
    selectedOptions,
    updateProductOption,
  } = usePdpContext();

  let defaultOption = '';
  let isSelectPartOfDefaultRecipe = false;
  let preselectedOption = '';
  const optionsList = new Map();
  // Create a map of all the selection options (along with needed details) that
  // can be chosen from when user makes a selection.
  // Also, while iterating through determine default option and if there is a
  // preselected option.
  const optionsDetailsMap = productOptionCategory.products.reduce(
    (options, item) => {
      defaultOption =
        defaultOption || (productOptionDefaults[item.productNumber] && item);

      if (productOptionDefaults[item.productNumber]?.inRecipe) {
        isSelectPartOfDefaultRecipe = true;
      }

      preselectedOption =
        preselectedOption ||
        (selectedOptions?.find(
          (option) => option.optionProductNumber === item.productNumber
        ) &&
          item);

      const optionSku = item?.form?.sizes?.[0]?.sku;
      const optionSizeCode = item?.form?.sizes?.[0]?.sizeCode;
      const optionName = item?.form?.name;
      if (optionSku && optionName)
        optionsList.set(optionSku, {
          name: optionName,
          sizeCode: optionSizeCode,
        });

      options[item?.form?.name] = item;
      return options;
    },
    {}
  );

  const productOptionPrice = isDemystifyMenuPdpEnabled
    ? pricedSelectedOptions?.get(preselectedOption?.form?.sizes?.[0]?.sku)
    : null;
  const defaultOptionName = defaultOption?.form?.name;
  const preselectedOptionName = preselectedOption?.form?.name;

  const modifiedFromDefault = preselectedOptionName
    ? defaultOptionName !== preselectedOptionName
    : false;

  const onSelectionHandler = (selectedName) => {
    const selectionDetails = optionsDetailsMap[selectedName];
    const selectedProductNumber =
      selectedName === NONE_VALUE
        ? NONE_VALUE
        : selectionDetails?.productNumber;

    const size = selectionDetails?.form?.sizes?.[0];
    const formCode = selectionDetails?.form?.formCode;

    updateProductOption({
      categoryKeypath: categoryKeyPath,
      optionProductNumber: selectedProductNumber,
      formCode,
      size,
      topLevelCategoryNumber,
      // we set this flag so that when handling updates to the same
      // category later, we know we can remove the previous choice
      // since this category has mutually exclusive options.
      exclusiveCategoryNum: productOptionCategory.categoryNumber,
    });
  };

  const getSelectId = () => {
    // for a OneSelect, metadata from the current category
    // creates a unique label. All of the children
    // of the current category will be represented in this OneSelect.
    return `${productOptionCategory.name
      .replace(/\s+/g, '-')
      .toLowerCase()}-one-select`;
  };

  const getLabel = () => {
    // An inactive OneSelect will not have a choice pre-selected,
    // so we want the label to represent the category,
    // ie. "Add Line the cup".
    // When a selection is made and the label moves to the
    // active position, we use the category name.
    if (useInactiveStyle) {
      return formatMessage(messages.addProductOption, {
        productOptionName: productOptionCategory?.name,
      });
    }
    return topLevelCategoryName;
  };

  return (
    <SelectWrapper
      addNoneOption={!isSelectPartOfDefaultRecipe}
      data-e2e="one-select"
      id={getSelectId()}
      isDemystifyMenuPdpEnabled={isDemystifyMenuPdpEnabled}
      modified={modifiedFromDefault}
      onSelection={onSelectionHandler}
      options={optionsList}
      pricedProductFullOptions={pricedProductFullOptions}
      productOptionPrice={productOptionPrice}
      useInactiveStyle={useInactiveStyle}
      value={preselectedOptionName || defaultOptionName}
    >
      {getLabel()}
    </SelectWrapper>
  );
};

OneSelect.propTypes = {
  categoryKeyPath: PropTypes.array,
  topLevelCategoryName: PropTypes.string,
  productOptionCategory: PropTypes.object,
  productOptionDefaults: PropTypes.object,
};

export default OneSelect;
